export interface CommentAuthor {
  id: string
  username: string
  display_name: string | null
  avatar_url: string | null
}

export interface CommentItem {
  id: string
  author: CommentAuthor
  content: string
  created_at: string
  updated_at: string
}

export const useCommentsStore = defineStore('comments', () => {
  const comments = ref<CommentItem[]>([])
  const total = ref(0)
  const currentPage = ref(1)
  const characterId = ref<string | null>(null)
  const isLoading = ref(false)

  const PAGE_SIZE = 5

  const totalPages = computed(() => Math.ceil(total.value / PAGE_SIZE))

  async function fetchComments(charId: string, page = 1) {
    isLoading.value = true
    try {
      const data = await $fetch<{ comments: CommentItem[], total: number }>(
        `/api/character/${charId}/comment`,
        { query: { page, limit: PAGE_SIZE } }
      )
      comments.value = data.comments
      total.value = data.total
      currentPage.value = page
      characterId.value = charId
      return data
    } catch {
      // Silent fail for public page viewing
      return { comments: comments.value, total: total.value }
    } finally {
      isLoading.value = false
    }
  }

  function addComment(comment: CommentItem) {
    comments.value.unshift(comment)
    total.value++
    if (comments.value.length > PAGE_SIZE) {
      comments.value.pop()
    }
  }

  function removeComment(commentId: string) {
    comments.value = comments.value.filter(c => c.id !== commentId)
    total.value = Math.max(0, total.value - 1)
  }

  function clear() {
    comments.value = []
    total.value = 0
    currentPage.value = 1
    characterId.value = null
  }

  return {
    comments,
    total,
    currentPage,
    characterId,
    isLoading,
    totalPages,
    PAGE_SIZE,
    fetchComments,
    addComment,
    removeComment,
    clear
  }
})
